import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Suspense, lazy, useEffect } from 'react'
import { Toaster } from 'sonner'

// Layout
import DashboardLayout from '@/components/layout/DashboardLayout'
import ProtectedRoute from '@/components/auth/ProtectedRoute'
import { useAuthStore } from '@/stores/authStore'
import NewVersionBanner from '@/components/NewVersionBanner'

// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-slate-50">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </div>
)

// Lazy load pages with error handling
const lazyWithRetry = (importFn: () => Promise<{ default: React.ComponentType }>) => {
  return lazy(() =>
    importFn().catch((error) => {
      console.error('Failed to load page:', error)
      // Return a fallback component instead of crashing
      return {
        default: () => (
          <div className="flex items-center justify-center h-full p-8">
            <div className="text-center">
              <p className="text-danger font-medium mb-2">Failed to load page</p>
              <p className="text-muted-foreground text-sm mb-4">{error?.message || 'Unknown error'}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
              >
                Reload Page
              </button>
            </div>
          </div>
        ),
      }
    })
  )
}

const LoginPage = lazyWithRetry(() => import('@/pages/Login'))
const DashboardPage = lazyWithRetry(() => import('@/pages/Dashboard'))
const SalesPage = lazyWithRetry(() => import('@/pages/Sales'))
const InventoryPage = lazyWithRetry(() => import('@/pages/Inventory'))
const InventoryTasksPage = lazyWithRetry(() => import('@/pages/InventoryTasks'))
const StaffPage = lazyWithRetry(() => import('@/pages/Staff'))
const StaffAccessPage = lazyWithRetry(() => import('@/pages/StaffAccess'))
const BranchesPage = lazyWithRetry(() => import('@/pages/Branches'))
const SalaryPage = lazyWithRetry(() => import('@/pages/Salary'))
const AttendancePage = lazyWithRetry(() => import('@/pages/Attendance'))
const ReportsPage = lazyWithRetry(() => import('@/pages/Reports'))
const NotificationsPage = lazyWithRetry(() => import('@/pages/Notifications'))
const NotificationSettingsPage = lazyWithRetry(() => import('@/pages/NotificationSettings'))
const ActivityPage = lazyWithRetry(() => import('@/pages/Activity'))
const SystemPage = lazyWithRetry(() => import('@/pages/System'))
const AnnouncementsPage = lazyWithRetry(() => import('@/pages/Announcements'))
const CalendarPage = lazyWithRetry(() => import('@/pages/Calendar'))
const GoalsPage = lazyWithRetry(() => import('@/pages/Goals'))
const SettingsPage = lazyWithRetry(() => import('@/pages/Settings'))
const SuppliersPage = lazyWithRetry(() => import('@/pages/Suppliers'))
const SupplierBalanceCheckPage = lazyWithRetry(() => import('@/pages/SupplierBalanceCheck'))
const EnrichmentReviewPage = lazyWithRetry(() => import('@/pages/EnrichmentReview'))
const CategoryMergePage = lazyWithRetry(() => import('@/pages/CategoryMerge'))
const BrandsManagerPage = lazyWithRetry(() => import('@/pages/BrandsManager'))
const ProfitPage = lazyWithRetry(() => import('@/pages/Profit'))
const StockAlertsPage = lazyWithRetry(() => import('@/pages/StockAlerts'))
const ExpensesPage = lazyWithRetry(() => import('@/pages/Expenses'))
const AdsManagerPage = lazyWithRetry(() => import('@/pages/AdsManager'))
const SocialPostsPage = lazyWithRetry(() => import('@/pages/SocialPosts'))
const PurchaseRequestsPage = lazyWithRetry(() => import('@/pages/PurchaseRequests'))
const PurchaseDemandPage = lazyWithRetry(() => import('@/pages/PurchaseDemand'))
const MercatoSearchPage = lazyWithRetry(() => import('@/pages/MercatoSearch'))
const DeliveryReconciliationPage = lazyWithRetry(() => import('@/pages/DeliveryReconciliation'))
const SearchAnalyticsPage = lazyWithRetry(() => import('@/pages/SearchAnalytics'))
const DrugIntelligencePage = lazyWithRetry(() => import('@/pages/DrugIntelligence'))
const PrescriptionCalculatorPage = lazyWithRetry(() => import('@/pages/PrescriptionCalculator'))
const MedicalRepsPage = lazyWithRetry(() => import('@/pages/MedicalReps'))
const BannerStudioPage = lazyWithRetry(() => import('@/pages/BannerStudio'))
const MercatoBrowsePage = lazyWithRetry(() => import('@/pages/MercatoBrowse'))
const MercatoSuppliersPage = lazyWithRetry(() => import('@/pages/MercatoSuppliers'))
const CapsulaCatalogPage = lazyWithRetry(() => import('@/pages/CapsulaCatalog'))
const CapsulaDisagreementsPage = lazyWithRetry(() => import('@/pages/CapsulaDisagreements'))
const CapsulaSuppliersPage = lazyWithRetry(() => import('@/pages/CapsulaSuppliers'))
const BsoLinkSuggestionsPage = lazyWithRetry(() => import('@/pages/BsoLinkSuggestions'))
const VshLinkSuggestionsPage = lazyWithRetry(() => import('@/pages/VshLinkSuggestions'))
const PharmazoneLinkSuggestionsPage = lazyWithRetry(() => import('@/pages/PharmazoneLinkSuggestions'))
const PosLinkSuggestionsPage = lazyWithRetry(() => import('@/pages/PosLinkSuggestions'))
const DuplicateReviewPage = lazyWithRetry(() => import('@/pages/DuplicateReview'))
const OrderItemMatchesPage = lazyWithRetry(() => import('@/pages/OrderItemMatchesPage'))
const CustomerFeedbackPage = lazyWithRetry(() => import('@/pages/CustomerFeedback'))
const LoyaltyPage = lazyWithRetry(() => import('@/pages/Loyalty'))
const ProductRequestsPage = lazyWithRetry(() => import('@/pages/ProductRequests'))
const AppVisibilityPage = lazyWithRetry(() => import('@/pages/AppVisibility'))
const AppConfigPage = lazyWithRetry(() => import('@/pages/AppConfig'))
const HomepageEditorPage = lazyWithRetry(() => import('@/pages/HomepageEditor'))
const PricingSettingsPage = lazyWithRetry(() => import('@/pages/PricingSettings'))
const MarginReviewPage = lazyWithRetry(() => import('@/pages/MarginReview'))
const PaymentRequestsPage = lazyWithRetry(() => import('@/pages/PaymentRequests'))
const PriceReviewsPage = lazyWithRetry(() => import('@/pages/PriceReviews'))
const MohSuggestionsPage = lazyWithRetry(() => import('@/pages/MohSuggestions'))
const MohMatchReviewPage = lazyWithRetry(() => import('@/pages/MohMatchReview'))
const DispenseSoonPage = lazyWithRetry(() => import('@/pages/DispenseSoon'))
const SupplementReviewQueuePage = lazyWithRetry(() => import('@/pages/SupplementReviewQueue'))

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

// Initialize auth on app load
function AuthInitializer({ children }: { children: React.ReactNode }) {
  const { initialize } = useAuthStore()

  useEffect(() => {
    initialize()
  }, [initialize])

  return <>{children}</>
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Toaster richColors position="top-right" closeButton expand visibleToasts={6} />
      <NewVersionBanner />
      <BrowserRouter>
        <AuthInitializer>
          <Routes>
            {/* Public routes - with suspense for login page */}
            <Route path="/login" element={
              <Suspense fallback={<PageLoader />}>
                <LoginPage />
              </Suspense>
            } />

            {/* Protected routes with dashboard layout (layout handles its own Suspense) */}
            <Route
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<DashboardPage />} />
              <Route path="/sales" element={<SalesPage />} />
              <Route path="/inventory" element={<InventoryPage />} />
              <Route path="/inventory-tasks" element={<InventoryTasksPage />} />
              <Route path="/staff" element={<StaffPage />} />
              <Route path="/staff/access" element={<StaffAccessPage />} />
              <Route path="/branches" element={<BranchesPage />} />
              <Route path="/salary" element={<SalaryPage />} />
              <Route path="/attendance" element={<AttendancePage />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/notification-settings" element={<NotificationSettingsPage />} />
              <Route path="/activity" element={<ActivityPage />} />
              <Route path="/system" element={<SystemPage />} />
              <Route path="/announcements" element={<AnnouncementsPage />} />
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/goals" element={<GoalsPage />} />
              <Route path="/suppliers" element={<SuppliersPage />} />
              <Route path="/supplier-balance-check" element={<SupplierBalanceCheckPage />} />
              <Route path="/profit" element={<ProfitPage />} />
              <Route path="/stock-alerts" element={<StockAlertsPage />} />
              <Route path="/dispense-soon" element={<DispenseSoonPage />} />
              <Route path="/enrichment" element={<EnrichmentReviewPage />} />
              <Route path="/category-merge" element={<CategoryMergePage />} />
              <Route path="/brands" element={<BrandsManagerPage />} />
              <Route path="/expenses" element={<ExpensesPage />} />
              <Route path="/ads" element={<AdsManagerPage />} />
              <Route path="/social" element={<SocialPostsPage />} />
              <Route path="/purchase-requests" element={<PurchaseRequestsPage />} />
              <Route path="/purchase-demand" element={<PurchaseDemandPage />} />
              <Route path="/mercato" element={<MercatoSearchPage />} />
              <Route path="/mercato-browse" element={<MercatoBrowsePage />} />
              <Route path="/mercato-suppliers" element={<MercatoSuppliersPage />} />
              <Route path="/capsula-catalog" element={<CapsulaCatalogPage />} />
              <Route path="/capsula-disagreements" element={<CapsulaDisagreementsPage />} />
              <Route path="/capsula-suppliers" element={<CapsulaSuppliersPage />} />
              <Route path="/bso-link" element={<BsoLinkSuggestionsPage />} />
              <Route path="/vsh-link" element={<VshLinkSuggestionsPage />} />
              <Route path="/pharmazone-link" element={<PharmazoneLinkSuggestionsPage />} />
              <Route path="/pos-link-suggestions" element={<PosLinkSuggestionsPage />} />
              <Route path="/duplicate-review" element={<DuplicateReviewPage />} />
              <Route path="/order-item-matches" element={<OrderItemMatchesPage />} />
              <Route path="/customer-feedback" element={<CustomerFeedbackPage />} />
              <Route path="/loyalty" element={<LoyaltyPage />} />
              <Route path="/product-requests" element={<ProductRequestsPage />} />
              <Route path="/delivery-reconciliation" element={<DeliveryReconciliationPage />} />
              <Route path="/search-analytics" element={<SearchAnalyticsPage />} />
              <Route path="/drug-intelligence" element={<DrugIntelligencePage />} />
              <Route path="/prescription-calculator" element={<PrescriptionCalculatorPage />} />
              <Route path="/medical-reps" element={<MedicalRepsPage />} />
              <Route path="/banner-studio" element={<BannerStudioPage />} />
              <Route path="/app-visibility" element={<AppVisibilityPage />} />
              <Route path="/app-config" element={<AppConfigPage />} />
              <Route path="/homepage-editor" element={<HomepageEditorPage />} />
              <Route path="/pricing-settings" element={<PricingSettingsPage />} />
              <Route path="/margin-review" element={<MarginReviewPage />} />
              <Route path="/payment-requests" element={<PaymentRequestsPage />} />
              <Route path="/price-reviews" element={<PriceReviewsPage />} />
              <Route path="/moh-prices" element={<MohSuggestionsPage />} />
              <Route path="/moh-match" element={<MohMatchReviewPage />} />
              <Route path="/supplement-review-queue" element={<SupplementReviewQueuePage />} />
              <Route path="/settings" element={<SettingsPage />} />
            </Route>

            {/* Catch all - redirect to dashboard */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthInitializer>
      </BrowserRouter>
    </QueryClientProvider>
  )
}

export default App
